import * as React from 'react';
import { reduxForm, InjectedFormProps, Field, getFormSyncErrors, change } from 'redux-form';
const { connect } = require('react-redux');
import { translate } from 'react-i18next';
import { IStore } from 'redux/IStore';
import { ErrorSummary, Modal, Button, WhiteButton } from 'components';
import { IModalAction } from 'models/modal';
import * as c from 'models/constants';
import { ImageUpload } from 'components/FormControls';
import { IImageData, ICropperOptions, ICropImageObject, ICropImageData } from 'models/common';
import { IAddImageData } from 'models/common/addimage';
import { clearAddImageFormError, getOriginalImage, requestFailure } from 'redux/modules/image';
import { ButtonsSection, PortalPageSection } from 'components/PortalStructure';
import { IModalSizes } from 'components/Modal';
import renderIcon, { IconName } from 'helpers/renderIcon';

require('./style.css');

interface IProps extends InjectedFormProps {
	addImageData: IAddImageData;
	initialValues: IAddImageData;
	currentFormValues: ICropImageObject;
	dispatch?: any;
	showHeaderCloseButton?: boolean;
	modalTitle: string;
	cropOptions: ICropperOptions;
	pictureUrl: string;
	type: string;
	entityReferenceId?: string;
	maxImageSize?: number;
	imageId?: string | undefined;
	modalSize?: IModalSizes['size'];
	imageType: 'profile' | 'shopBanner' | 'userBanner' | 'article'; // shop and user banners have different ratios

	/* form props */
	pristine: boolean;
	submitting: boolean;
	submitFailed: boolean;
	handleSubmit: any;
	formErrors?: any;
	submit: (formName: string) => void;
	change: (field: string, value: any) => void;

	/* functions props */
	saveImage: (form: ICropImageObject) => void;
	clearAddImageFormError: () => void;
	requestFailure: (error: any) => void;
	t?: (key: string) => string;
	closeModal?: () => IModalAction;
	getOriginalImage?: (imageId: string) => void;
}

interface IState {
	uploadButtonClicked: boolean;
	editButtonClicked: boolean;
}

@translate(c.DEFAULT_TRANSLATION_NAMESPACE)
class CropImageModalForm extends React.Component<IProps, IState> {
	private imageInputRef: any;

	constructor(props) {
		super(props);
		this.state = {
			uploadButtonClicked: false,
			editButtonClicked: false,
		};

		this.onSubmit = this.onSubmit.bind(this);
		this.closeModal = this.closeModal.bind(this);
		this.mainImageUploadedCallback = this.mainImageUploadedCallback.bind(this);
		this.croppedImageCallback = this.croppedImageCallback.bind(this);
		this.deleteMainImage = this.deleteMainImage.bind(this);
		this.handleEdit = this.handleEdit.bind(this);
		this.handleUploadButtonClick = this.handleUploadButtonClick.bind(this);
		this.imageUploadFailedCallback = this.imageUploadFailedCallback.bind(this);
		this.imageInputRef = React.createRef();
	}

	public componentDidMount(): void {
		if (this.props.imageId && this.props.getOriginalImage) {
			this.props.getOriginalImage(this.props.imageId);
		}
	}

	public componentDidUpdate(prevProps: Readonly<IProps>): void {
		if (prevProps.imageId !== this.props.imageId && this.props.imageId && this.props.getOriginalImage) {
			this.props.getOriginalImage(this.props.imageId);
		}
	}

	public componentWillUnmount() {
		this.props.clearAddImageFormError();
	}

	public onSubmit(form: ICropImageData) {
		const { imageType } = this.props;
		form = { ...form };
		form.entityType = this.props.type;
		if (this.props.entityReferenceId) {
			form.entityReferenceId = this.props.entityReferenceId;
		}

		// thumbnail
		form.createThumbnail = true;
		form.thumbnails = [];
		let sizes = [];

		switch (imageType) {
			case 'article':
				sizes = c.DEFAULT_CROP_SIZES_ARTICLE_IMAGE;
				break;
			case 'profile':
				sizes = c.DEFAULT_CROP_SIZES_PROFILE_IMAGE;
				break;
			case 'shopBanner':
				sizes = c.DEFAULT_CROP_SIZES_SHOP_BANNER;
				break;
			case 'userBanner':
				sizes = c.DEFAULT_CROP_SIZES_USER_BANNER;
				break;
			default:
				sizes = c.DEFAULT_CROP_SIZES_PROFILE_IMAGE;
		}

		// const sizes =
		// 	this.props.imageType === 'banner' ? c.DEFAULT_CROP_SIZES_USER_BANNER : c.DEFAULT_CROP_SIZES_PROFILE_IMAGE;

		const ratio = form.width / form.height;
		sizes.forEach((size) => {
			const sizeRatio = size.thumbnailWidth / size.thumbnailHeight;

			if (sizeRatio < ratio) {
				size.thumbnailWidth = Math.round((ratio / sizeRatio) * size.thumbnailWidth);
			}

			form.thumbnails.push(size);
		});

		this.props.saveImage(form);
		this.setState({ uploadButtonClicked: false, editButtonClicked: false });
	}

	private mainImageUploadedCallback(id: string, href: string, additionalData: string[]) {
		const image: IImageData = {
			id,
			href,
		};

		if (additionalData.length > 0) {
			this.props.change(additionalData[0], image.id);
			this.props.change(additionalData[1], image.href);
		}

		this.props.clearAddImageFormError();
		this.setState({ uploadButtonClicked: true });
		this.forceUpdate();
	}

	private imageUploadFailedCallback(error: any) {
		this.setState({ uploadButtonClicked: false });
		if (error) {
			this.props.requestFailure(error);
			this.forceUpdate();
		}
	}

	private croppedImageCallback(imageData: ICropImageObject) {
		this.props.change('x', imageData.x);
		this.props.change('y', imageData.y);
		this.props.change('width', imageData.width);
		this.props.change('height', imageData.height);
		this.props.change('rotate', imageData.rotate);
	}

	private deleteMainImage(_, additionalData: string[]) {
		this.props.change(additionalData[0], null);
		this.props.change(additionalData[1], null);
		this.props.change('x', null);
		this.props.change('y', null);
		this.props.change('width', null);
		this.props.change('height', null);
		this.props.change('rotate', null);
		this.forceUpdate();
	}

	private closeModal() {
		this.props.closeModal();
		this.setState({ uploadButtonClicked: false, editButtonClicked: false });
	}

	public handleEdit() {
		this.setState({ editButtonClicked: true });
	}

	public handleUploadButtonClick() {
		if (this.imageInputRef.current) {
			this.imageInputRef.current.open();
		}
	}

	private mapErrorKeyToMessage(key: string) {
		switch (key) {
			case 'imageUpload':
				return 'Image upload';
			default:
				return key;
		}
	}

	/** --------------RENDER ----------------------- */
	public render() {
		const {
			pristine,
			submitting,
			handleSubmit,
			t,
			formErrors,
			submitFailed,
			showHeaderCloseButton,
			modalTitle,
			cropOptions,
			addImageData,
			modalSize,
			imageType,
		} = this.props;
		const { error, message } = this.props.addImageData;
		const editableModalSize: IModalSizes['size'] = 'normal';
		const editingModalTitle =
			imageType === 'profile' ? t('User_UserCard_EditProfilePicture') : t('User_UserCard_EditBannerPicture');

		const errorSummaryProps = {
			formErrors,
			submitFailed,
			error,
			message,
			location,
			mapErrorKeyToMessage: this.mapErrorKeyToMessage,
		};

		cropOptions.croppedImageCallback = this.croppedImageCallback;
		cropOptions.showSlider = true;

		let subtextDimensions = '';

		switch (imageType) {
			case 'article':
				subtextDimensions =
					c.DEFAULT_CROP_SIZES_ARTICLE_IMAGE[0].thumbnailWidth +
					' x ' +
					c.DEFAULT_CROP_SIZES_ARTICLE_IMAGE[0].thumbnailHeight;
				break;
			case 'profile':
				subtextDimensions =
					c.DEFAULT_CROP_SIZES_PROFILE_IMAGE[0].thumbnailWidth +
					' x ' +
					c.DEFAULT_CROP_SIZES_PROFILE_IMAGE[0].thumbnailHeight;
				break;
			case 'shopBanner':
				subtextDimensions =
					c.DEFAULT_CROP_SIZES_SHOP_BANNER[0].thumbnailWidth +
					' x ' +
					c.DEFAULT_CROP_SIZES_SHOP_BANNER[0].thumbnailHeight;
				break;
			case 'userBanner':
				subtextDimensions =
					c.DEFAULT_CROP_SIZES_USER_BANNER[0].thumbnailWidth +
					' x ' +
					c.DEFAULT_CROP_SIZES_USER_BANNER[0].thumbnailHeight;
				break;
			default:
				subtextDimensions =
					c.DEFAULT_CROP_SIZES_USER_BANNER[0].thumbnailWidth +
					' x ' +
					c.DEFAULT_CROP_SIZES_USER_BANNER[0].thumbnailHeight;
		}

		const clickableElement = (
			text: string,
			iconName: IconName,
			action: (image?: ICropImageData) => void,
			subText?: string | undefined,
			disabled?: boolean,
			hasErrors?: boolean,
		): JSX.Element => {
			return (
				<div
					className={`clickableCard large ${disabled ? 'disabled' : ''} ${hasErrors ? 'hasErrors' : ''}`}
					onClick={() => action()}
				>
					<div className="text-container">
						<p className="main">{text}</p>
						{subText ? <p className="muted">{subText}</p> : null}
					</div>
					{renderIcon(iconName, 32, 32, '')}
				</div>
			);
		};
		const subtext = `JPEG, PNG (${t('User_UserCard_Optimal')}: ${subtextDimensions}, < 4MB)`;
		const footer = (
			<>
				{!this.state.editButtonClicked && !this.state.uploadButtonClicked ? (
					<WhiteButton onClick={this.closeModal}>{t('BSPortal_Common_Cancel')}</WhiteButton>
				) : (
					<ButtonsSection align="right">
						<WhiteButton onClick={this.closeModal}>{t('BSPortal_Common_Close')}</WhiteButton>
						<Button
							onClick={handleSubmit((values) => {
								this.onSubmit(values);
							})}
							disabled={pristine || submitting}
						>
							{t('BSPortal_Common_Apply')}
						</Button>
					</ButtonsSection>
				)}
			</>
		);

		const isEditingPhoto = Boolean(
			this.props.currentFormValues.id && (this.state.editButtonClicked || this.state.uploadButtonClicked),
		);

		return (
			<Modal
				modalClass="modal-change-photo-wrapper"
				title={isEditingPhoto ? editingModalTitle : modalTitle}
				showHeaderCloseButton={showHeaderCloseButton}
				closeModal={this.closeModal}
				size={isEditingPhoto ? editableModalSize : modalSize}
				footer={footer}
			>
				<form onSubmit={handleSubmit(this.onSubmit)} className={`${isEditingPhoto ? '' : 'hidden'}`}>
					<PortalPageSection>
						<div>
							<ErrorSummary {...errorSummaryProps} />
						</div>
						<Field
							name="imageUpload"
							dropZoneRef={this.imageInputRef}
							withRef={true}
							component={ImageUpload}
							imageUploadedCallback={this.mainImageUploadedCallback}
							imageUploadFailed={this.imageUploadFailedCallback}
							additionalData={['id', 'url']}
							existingImageId={isEditingPhoto ? this.props.currentFormValues.id : null}
							existingImageUrl={isEditingPhoto ? this.props.currentFormValues.url : null}
							cropImage={true}
							cropOptions={cropOptions}
							maxImageSize={10000000}
						/>
					</PortalPageSection>
				</form>
				{!this.state.editButtonClicked && !this.state.uploadButtonClicked ? (
					<>
						{clickableElement(
							t('User_UserCard_EditCurrentPicture'),
							'edit',
							this.handleEdit,
							undefined,
							!this.props.currentFormValues.id || addImageData.isFetching,
						)}
						{clickableElement(
							t('User_UserCard_UploadNewPicture'),
							'upload',
							this.handleUploadButtonClick,
							subtext,
							addImageData.isFetching,
							addImageData.error,
						)}
						<br />
						<br />
						{addImageData.error && addImageData.message?.error?.[0] ? (
							<p className="p14 red">{addImageData.message?.error[0]}</p>
						) : addImageData.error && addImageData.message?.statusText ? (
							<p className="p14 red">{addImageData.message?.statusText}</p>
						) : null}
					</>
				) : null}
			</Modal>
		);
	}
}

function mapStateToProps(state: IStore) {
	const imageData: IAddImageData = state.image;

	let currentFormValues: IImageData = null;
	if (state.form[c.FORM_NAME_ADD_IMAGE] && state.form[c.FORM_NAME_ADD_IMAGE].values) {
		currentFormValues = state.form[c.FORM_NAME_ADD_IMAGE].values;
	} else {
		currentFormValues = imageData.image;
	}

	return {
		addImageData: imageData,
		initialValues: imageData.image,
		currentFormValues,
		formErrors: getFormSyncErrors(c.FORM_NAME_ADD_IMAGE)(state),
	};
}

let CropImageModal = reduxForm(
	{
		form: c.FORM_NAME_ADD_IMAGE,
	},
	mapStateToProps,
)(CropImageModalForm);

CropImageModal = connect(mapStateToProps, (dispatch) => ({
	change: (field: string, value: any) => dispatch(change(field, value)),
	clearAddImageFormError: () => dispatch(clearAddImageFormError()),
	requestFailure: (error) => dispatch(requestFailure(error)),
	getOriginalImage: (imageId: string) => dispatch(getOriginalImage(imageId, c.FORM_NAME_ADD_IMAGE)),
}))(CropImageModal);

export { CropImageModal };
